// Release B · B1 frontend — Public Request Portal Access page.
// Route: /request-access
// ERF brand palette: deep navy, blueprint blue, warm cream, restrained gold.
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";

const NAVY = "#12233F";
const CREAM = "#F5F1E8";
const GOLD = "#C6A15B";

// R1e-Freeze-A2P — verbatim SMS opt-in disclosure text for the Portal Access
// Request form. Distinct from `consent_communications` (email/phone contact
// about this specific request). Any change here MUST bump the backend
// `terms_version` / `policy_version` constants.
export const REQUEST_ACCESS_SMS_DISCLOSURE_TEXT =
  "By checking this box, I agree to receive SMS messages from ERF ENTERPRISES LLC concerning quote requests, appointments, project updates, account notifications and customer support. Message frequency varies. Message and data rates may apply. Reply STOP to opt out or HELP for help. Consent is not a condition of purchase. See our Privacy Policy and Terms and Conditions.";

export default function RequestAccess() {
  const [rels, setRels] = useState([]);
  const [form, setForm] = useState({
    full_legal_name: "", preferred_name: "", business_name: "",
    email: "", phone: "", city: "", state: "",
    requested_relationship: "", other_relationship_description: "",
    referrer_name: "", reason: "",
    consent_communications: false, consent_truthful_info: false,
    // R1e-Freeze-A2P — optional SMS opt-in, never preselected.
    sms_consent_granted: false,
    honeypot: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    api.get("/public/access-requests/relationships")
      .then((r) => setRels(r.data.options || []))
      .catch(() => setRels([]));
  }, []);

  const update = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    // Client validation.
    const req = ["full_legal_name", "email", "phone", "city", "state",
                  "requested_relationship", "reason"];
    for (const k of req) if (!(form[k] || "").trim()) {
      setError(`Please complete every required field. Missing: ${k.replace(/_/g, " ")}.`); return;
    }
    if (form.requested_relationship === "other" && !form.other_relationship_description.trim()) {
      setError("Please describe your relationship."); return;
    }
    if (!form.consent_communications || !form.consent_truthful_info) {
      setError("Both acknowledgements are required to submit."); return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/public/access-requests", {
        ...form,
        // R1e-Freeze-A2P — only forward disclosure text when opt-in checked.
        sms_consent_disclosure_text: form.sms_consent_granted
          ? REQUEST_ACCESS_SMS_DISCLOSURE_TEXT : null,
      });
      setSuccess(r.data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(d?.message || d?.code || "Submission failed. Please try again.");
    } finally { setSubmitting(false); }
  };

  if (success) return (
    <main data-testid="request-access-success" style={{ minHeight: "100vh", background: CREAM, color: NAVY }}>
      <div style={{ maxWidth: 640, margin: "0 auto", padding: "80px 24px" }}>
        <div style={{ borderTop: `4px solid ${GOLD}`, paddingTop: 24 }}>
          <h1 style={{ fontFamily: "Playfair Display, serif", fontSize: 34, margin: 0 }}>Thank you</h1>
          <p style={{ marginTop: 16, fontSize: 16 }}>{success.message}</p>
          <p style={{ marginTop: 8, fontSize: 13, color: "#475569" }}>
            Reference: <b data-testid="request-access-reference">{success.reference}</b>
          </p>
          <p style={{ marginTop: 24, fontSize: 12, color: "#64748b" }}>
            Keep an eye on your inbox — the confirmation link expires in 48 hours. Do not share your reference number or the link publicly.
          </p>
          <Link to="/" data-testid="request-access-home-link" style={{ display: "inline-block", marginTop: 20, color: NAVY, textDecoration: "underline" }}>← Return to ERF Enterprises</Link>
        </div>
      </div>
    </main>
  );

  return (
    <main data-testid="request-access-page" style={{ minHeight: "100vh", background: CREAM, color: NAVY }}>
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "48px 20px" }}>
        <div style={{ borderTop: `4px solid ${GOLD}`, paddingTop: 24 }}>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: 2, color: GOLD, textTransform: "uppercase" }}>ERF ConstructionOS · Portal Access</div>
          <h1 style={{ fontFamily: "Playfair Display, serif", fontSize: 34, margin: "8px 0 4px", lineHeight: 1.1 }}>Request access to ERF ConstructionOS</h1>
          <p style={{ marginTop: 8, color: "#475569", fontSize: 14, maxWidth: 620 }}>
            There is no self-service sign-up. Edwin (or an authorized Administrator) reviews every request and grants access only to the specific role you have with ERF. Your submission is stored in a tenant-isolated record; no one else sees it.
          </p>
        </div>

        <form onSubmit={submit} noValidate style={{ marginTop: 28, display: "grid", gap: 14 }}>
          {/* honeypot */}
          <input type="text" name="honeypot" value={form.honeypot} onChange={(e) => update("honeypot", e.target.value)}
                 aria-hidden="true" tabIndex={-1} autoComplete="off"
                 style={{ position: "absolute", left: "-9999px", opacity: 0, pointerEvents: "none" }} />

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <Field label="Full legal name *" testid="ra-name">
              <input value={form.full_legal_name} onChange={(e) => update("full_legal_name", e.target.value)}
                     required aria-required style={inputStyle} data-testid="ra-name-input" />
            </Field>
            <Field label="Preferred name">
              <input value={form.preferred_name} onChange={(e) => update("preferred_name", e.target.value)}
                     style={inputStyle} data-testid="ra-preferred-input" />
            </Field>
          </div>
          <Field label="Business or organization name">
            <input value={form.business_name} onChange={(e) => update("business_name", e.target.value)}
                   style={inputStyle} data-testid="ra-business-input" />
          </Field>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <Field label="Work email *">
              <input type="email" value={form.email} onChange={(e) => update("email", e.target.value)}
                     required aria-required style={inputStyle} data-testid="ra-email-input" />
            </Field>
            <Field label="Phone *">
              <input value={form.phone} onChange={(e) => update("phone", e.target.value)}
                     required aria-required style={inputStyle} data-testid="ra-phone-input" />
            </Field>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <Field label="City *">
              <input value={form.city} onChange={(e) => update("city", e.target.value)}
                     required aria-required style={inputStyle} data-testid="ra-city-input" />
            </Field>
            <Field label="State *">
              <input value={form.state} onChange={(e) => update("state", e.target.value)}
                     required aria-required style={inputStyle} data-testid="ra-state-input" />
            </Field>
          </div>
          <Field label="Requested relationship *">
            <select value={form.requested_relationship}
                    onChange={(e) => update("requested_relationship", e.target.value)}
                    required aria-required data-testid="ra-relationship-select"
                    style={inputStyle}>
              <option value="">Choose the relationship that best fits…</option>
              {rels.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
          </Field>
          {form.requested_relationship === "other" && (
            <Field label="Please describe your relationship *">
              <input value={form.other_relationship_description}
                     onChange={(e) => update("other_relationship_description", e.target.value)}
                     required aria-required data-testid="ra-other-desc-input"
                     style={inputStyle} />
            </Field>
          )}
          <Field label="ERF referrer (optional)">
            <input value={form.referrer_name} onChange={(e) => update("referrer_name", e.target.value)}
                   style={inputStyle} data-testid="ra-referrer-input" />
          </Field>
          <Field label="Why are you requesting access? *">
            <textarea value={form.reason} onChange={(e) => update("reason", e.target.value)}
                      required aria-required rows={5} data-testid="ra-reason-input"
                      style={{ ...inputStyle, resize: "vertical", minHeight: 100 }} />
          </Field>
          <div style={{ display: "grid", gap: 6, fontSize: 13, marginTop: 6 }}>
            <label style={{ display: "flex", gap: 8, alignItems: "flex-start" }} data-testid="ra-consent-comms-label">
              <input type="checkbox" checked={form.consent_communications}
                     onChange={(e) => update("consent_communications", e.target.checked)}
                     data-testid="ra-consent-comms-input" style={{ marginTop: 3 }} />
              <span>I consent to receive email and/or phone contact from ERF Enterprises about this request.</span>
            </label>
            <label style={{ display: "flex", gap: 8, alignItems: "flex-start" }} data-testid="ra-consent-truthful-label">
              <input type="checkbox" checked={form.consent_truthful_info}
                     onChange={(e) => update("consent_truthful_info", e.target.checked)}
                     data-testid="ra-consent-truthful-input" style={{ marginTop: 3 }} />
              <span>I confirm that all information above is accurate and truthful.</span>
            </label>
            {/* R1e-Freeze-A2P — Optional SMS opt-in, separate from the
                required consents above. Never preselected. Submission
                succeeds whether or not this box is checked. */}
            <div
              data-testid="ra-sms-consent-block"
              style={{
                marginTop: 6, paddingTop: 12,
                borderTop: "1px solid #E5E1DA",
              }}
            >
              <label
                htmlFor="ra-sms-consent"
                style={{ display: "flex", gap: 8, alignItems: "flex-start", cursor: "pointer" }}
                data-testid="ra-sms-consent-label"
              >
                <input
                  id="ra-sms-consent"
                  name="sms_consent_granted"
                  type="checkbox"
                  defaultChecked={false}
                  checked={form.sms_consent_granted}
                  onChange={(e) => update("sms_consent_granted", e.target.checked)}
                  data-testid="ra-sms-consent-input"
                  style={{ marginTop: 3 }}
                />
                <span data-testid="ra-sms-consent-disclosure">
                  By checking this box, I agree to receive SMS messages from
                  ERF ENTERPRISES LLC concerning quote requests, appointments,
                  project updates, account notifications and customer support.
                  Message frequency varies. Message and data rates may apply.
                  Reply <strong>STOP</strong> to opt out or <strong>HELP</strong> for help.
                  Consent is not a condition of purchase. See our{" "}
                  <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" data-testid="ra-sms-privacy-link" style={{ color: NAVY, textDecoration: "underline" }}>
                    Privacy Policy
                  </a>{" "}
                  and{" "}
                  <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer" data-testid="ra-sms-terms-link" style={{ color: NAVY, textDecoration: "underline" }}>
                    Terms and Conditions
                  </a>
                  .
                </span>
              </label>
              <p
                data-testid="ra-sms-optional-helper"
                style={{ marginTop: 6, fontSize: 11, color: "#64748b", fontStyle: "italic" }}
              >
                Optional. You can submit this form without agreeing to SMS.
              </p>
            </div>
          </div>
          {error && (
            <div role="alert" aria-live="assertive" data-testid="ra-error"
                 style={{ color: "#991b1b", background: "#fee2e2", padding: 10, borderRadius: 6, fontSize: 13 }}>
              {error}
            </div>
          )}
          <button type="submit" disabled={submitting} data-testid="ra-submit"
                  style={{ marginTop: 12, padding: "14px 22px", background: NAVY, color: CREAM,
                            border: `2px solid ${GOLD}`, cursor: submitting ? "wait" : "pointer",
                            fontSize: 14, letterSpacing: 1, textTransform: "uppercase",
                            fontFamily: "'Space Mono', monospace" }}>
            {submitting ? "Submitting…" : "Submit request"}
          </button>
          <p style={{ fontSize: 11, color: "#64748b", marginTop: 6 }}>
            Requesting access is not a guarantee of access. Every submission is logged.
            <br /><Link to="/login" style={{ color: NAVY, textDecoration: "underline" }}>Already have an account? Sign in.</Link>
          </p>
        </form>
      </div>
    </main>
  );
}

const inputStyle = {
  width: "100%", padding: "10px 12px", border: `1px solid #cbd5e1`,
  borderRadius: 4, fontSize: 14, background: "white", color: "#0f172a",
  boxSizing: "border-box",
};

function Field({ label, children, testid }) {
  return (
    <label data-testid={testid} style={{ display: "grid", gap: 6, fontSize: 12,
             textTransform: "uppercase", letterSpacing: 1, color: "#475569",
             fontFamily: "'Space Mono', monospace" }}>
      <span>{label}</span>
      {children}
    </label>
  );
}
